import React, { useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "../App.css";
import { baseurlvmkp } from "../Files/Subjects/Baseurl";

// ================= URLS =================
const BASE_URL = "/vmkp";
const API_URL = baseurlvmkp;

// ================= AXIOS =================
const publicApi = axios.create({ baseURL: BASE_URL });
const privateApi = axios.create({ baseURL: API_URL });

privateApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("staffToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ================= HELPERS =================
const setCell = (row, headers, key, value) => {
  if (!headers.includes(key)) headers.push(key);
  row[key] = value ?? "";
};

const IGNORE_KEYS = ["id", "userId", "name", "createdAt", "updatedAt"];

// ================= SUBJECT DATA =================
const extractByObjectKeys = (row, headers, subjectData) => {
  if (!subjectData) return;

  Object.keys(subjectData).forEach((areaKey) => {
    if (IGNORE_KEYS.includes(areaKey)) return;

    const section = subjectData[areaKey];

    const questions =
      section?.questions ||
      section?.healthQuestion ||
      section?.wealthQuestion ||
      section?.workEducationQuestions ||
      section?.selfAwarenessQuestions ||
      [];

    const bucket = {
      Applicable: "",
      Working: "",
      Critical: "",
      Future: "",
    };

    questions.forEach((q) => {
      const ans =
        q?.answer?.text ||
        q?.healthAnswer?.text ||
        q?.wealthAnswer?.text ||
        q?.workEducationAnswer?.text ||
        q?.selfAwarenessAnswer?.text ||
        "";

      if (["Applicable", "Not Applicable"].includes(ans))
        bucket.Applicable ||= ans;

      if (["Working", "Not Working"].includes(ans))
        bucket.Working ||= ans;

      if (
        [
          "Not Critical",
          "Somewhat Critical",
          "Critical",
          "Very Critical",
          "Most Critical",
        ].includes(ans)
      )
        bucket.Critical ||= ans;

      if (
        ["Necessity", "Wish/Want/Desire", "Dream", "Hope", "Miracle"].includes(ans)
      )
        bucket.Future ||= ans;
    });

    const base = areaKey.toUpperCase();

    setCell(row, headers, `${base}_APPLICABLE`, bucket.Applicable);
    setCell(row, headers, `${base}_WORKING`, bucket.Working);
    setCell(row, headers, `${base}_CRITICAL`, bucket.Critical);
    setCell(row, headers, `${base}_FUTURE`, bucket.Future);
  });
};

// ================= SCORE DATA =================
const extractScores = (row, headers, score) => {
  const scores = {
    APPLICABILITY_SCORE: score.applicability,
    CRITICALITY_SCORE: score.criticality,
    WORKABILITY_SCORE: score.workability,

    FUTURALITY_SCORE: score.futurality,
    RELATIONSHIPS_SCORE: score.relationships,

    HEALTH_SCORE: score.health,
    WEALTH_SCORE: score.wealth,
    WORK_EDUCATION_SCORE: score.workEducation,

    SELF_AWARENESS_SCORE: score.selfAwareness,
    AVERAGE_SCORE: score.average,
    Y_AXIS_SCORE: score.y_AXIS,

    X_AXIS_SCORE: score.x_AXIS,
  };

  Object.entries(scores).forEach(([k, v]) =>
    setCell(row, headers, k, v ?? 0)
  );
};

// ================= COMPONENT =================
export default function ExamFullExcelExport() {
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);

  const exportExcel = async () => {
    if (!date) return alert("Select date");

    try {
      setLoading(true);
      setDone(0);

      const rows = [];
      const headers = [];

      // 1️⃣ GET CU
      const cuRes = await publicApi.get(`/excel/getcu/${date}/${date}`);
      const cuRows = cuRes.data || [];
      setTotal(cuRows.length);

      for (const cu of cuRows) {
        if (!cu.examid) continue;

        const row = {};

        // BASE DETAILS
        Object.entries({
          EXAM_ID: cu.examid,
          FULL_NAME: cu.fullname,
          EMAIL: cu.emailid,
          PHONE: cu.contactnumber,
          GENDER: cu.gender,
          AGE: cu.age,
          MARITAL_STATUS: cu.maritalstatus,
          OCCUPATION: cu.occupation,
          ADDRESS: cu.address,
          CITY: cu.city,
          STATE: cu.state,
          COUNTRY: cu.country,
          EXAM_STATUS: cu.examstatus,
          START_DATE: cu.examstartdate,
          START_TIME: cu.starttime,
          END_DATE: cu.enddate,
          END_TIME: cu.endtime,
          COUPON: cu.couponname,
          PAYMENT_AMOUNT: cu.paymentamount,
        }).forEach(([k, v]) => setCell(row, headers, k, v));

        // 2️⃣ GET EXAM
        const exam = (
          await privateApi.get(`/exam/getexam/${cu.examid}`)
        ).data;

        // 3️⃣ GET SUBJECT SECTIONS
        const [r, h, w, we, s] = await Promise.all([
          exam.relationshipid &&
          privateApi.get(`/relationship/get/${exam.relationshipid}`),
          exam.healthid &&
          privateApi.get(`/health/get/${exam.healthid}`),
          exam.wealthid &&
          privateApi.get(`/wealth/get/${exam.wealthid}`),
          exam.workeducationid &&
          privateApi.get(`/workeducation/get/${exam.workeducationid}`),
          exam.selfawarenessid &&
          privateApi.get(`/selfawareness/get/${exam.selfawarenessid}`),
        ]);

        if (r) extractByObjectKeys(row, headers, r.data);
        if (h) extractByObjectKeys(row, headers, h.data);
        if (w) extractByObjectKeys(row, headers, w.data);
        if (we) extractByObjectKeys(row, headers, we.data);
        if (s) extractByObjectKeys(row, headers, s.data);

        // 4️⃣ GET SCORES
        const scoreRes = await privateApi.get(
          `/index/get/${exam.userId}/${cu.examid}`
        );

        let score = {};
        if (Array.isArray(scoreRes.data)) {
          score = scoreRes.data[0] || {};
        } else {
          score = scoreRes.data?.data || scoreRes.data || {};
        }

        extractScores(row, headers, score);

        rows.push(row);
        setDone((d) => d + 1);
      }

      // FORCE HEADERS
      rows.unshift(headers.reduce((a, h) => ({ ...a, [h]: "" }), {}));

      // EXPORT
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Full Exam Report");

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf]), `Exam_Full_Report_${date}.xlsx`);
    } catch (e) {
      console.error(e);
      alert("Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Full Exam Excel Export</h2>
          <p style={styles.subtitle}>
            Export complete exam data with answers and scores
          </p>
        </div>

        <div style={styles.body}>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Exam Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={styles.input}
              />
            </div>

            <button
              onClick={exportExcel}
              disabled={loading}
              style={{
                ...styles.button,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Exporting..." : "Export Excel"}
            </button>
          </div>

          {loading && (
            <div style={styles.progressBox}>
              <div style={styles.progressText}>
                Processing records {done} / {total}
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${Math.round((done / total) * 100) || 0}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

}
const styles = {
  page: {
    // display: "flex",
    // justifyContent: "center",
    // padding: "40px 20px",
    width: "100%",
    maxWidth: 900,
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  },

  // card: {
  //   width: "100%",
  //   maxWidth: 900,
  //   background: "#fff",
  //   borderRadius: 14,
  //   boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  // },

  header: {
    padding: "24px 28px",
    borderBottom: "1px solid #eee",
  },

  title: {
    margin: 0,
    fontSize: 22,
    color: "#333",
    fontWeight: 600,
  },

  subtitle: {
    marginTop: 6,
    fontSize: 14,
    color: "#666",
  },

  body: {
    padding: "28px",
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },

  row: {
    display: "flex",
    alignItems: "flex-end",
    gap: 20,
    flexWrap: "wrap",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 220,
  },

  label: {
    fontSize: 13,
    color: "#444",
    fontWeight: 500,
  },

  input: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #d0d5dd",
    fontSize: 14,
  },

  button: {
    height: 42,
    padding: "0 22px",
    borderRadius: 10,
    border: "none",
    background: "#4f6ef7",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
  },

  progressBox: {
    background: "#f4f6fb",
    borderRadius: 10,
    padding: 16,
  },

  progressText: {
    fontSize: 14,
    marginBottom: 8,
    color: "#333",
  },

  progressBar: {
    height: 8,
    background: "#e0e5ff",
    borderRadius: 10,
    overflow: "hidden",
  },

  progressFill: {
    height: "100%",
    background: "#4f6ef7",
    transition: "width 0.3s ease",
  },
};
